/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useState, createRef, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { Dropdown, Input } from 'semantic-ui-react';
import queryString from 'query-string';
import classnames from 'classnames';
import * as AppClasses from './styles.module.scss';
import * as Classes from './search.module.scss';
import { THUMB } from '../walls/grid_selection';
import { loadAutocompleteDataWithCache } from '../../config/api';

const WALLS = 'walls';
const WORKS = 'art-prints';

const OPTIONS = [
  { key: WORKS, text: 'Art', value: WORKS },
  { key: WALLS, text: 'Walls', value: WALLS }
];

// Calculate Levenshtein distance between two strings
const levenshteinDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  for (let i = 0; i <= b.length; i += 1) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i += 1) {
    for (let j = 1; j <= a.length; j += 1) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

// Find best substring match in name (exact or with 1 typo)
// Returns { index, length, distance } or null if no match
const findBestMatch = (searchQuery, name) => {
  if (!searchQuery || !name) return null;

  const lowerQuery = searchQuery.toLowerCase();
  const lowerName = name.toLowerCase();

  // First check for exact substring match
  const exactIndex = lowerName.indexOf(lowerQuery);
  if (exactIndex !== -1) {
    return { index: exactIndex, length: lowerQuery.length, distance: 0 };
  }

  // Check for fuzzy match with 1 typo
  // Try substrings of lengths: query.length - 1, query.length, query.length + 1
  let bestMatch = null;

  const minLen = Math.max(1, lowerQuery.length - 1);
  const maxLen = lowerQuery.length + 1;

  for (let len = minLen; len <= maxLen; len += 1) {
    for (let i = 0; i <= lowerName.length - len; i += 1) {
      const substring = lowerName.substring(i, i + len);
      const distance = levenshteinDistance(lowerQuery, substring);

      if (distance === 1) {
        // Prefer earlier matches and exact length matches
        if (
          !bestMatch
          || (len === lowerQuery.length && bestMatch.length !== lowerQuery.length)
          || (len === bestMatch.length && i < bestMatch.index)
        ) {
          bestMatch = { index: i, length: len, distance };
        }
      }
    }
  }

  return bestMatch;
};

// Calculate match score: exact substring or 1 typo allowed
// Returns position-based score (earlier matches score higher), or 0 if no match
const calculateMatchScore = (searchQuery, name) => {
  const match = findBestMatch(searchQuery, name);
  if (!match) return 0;

  // Exact matches score higher than typo matches
  // Earlier position scores higher
  const baseScore = match.distance === 0 ? 2000 : 1000;
  return baseScore - match.index;
};

// Highlight matching substring in artist name
const highlightMatch = (searchQuery, name) => {
  if (!searchQuery || !name) return name;

  const match = findBestMatch(searchQuery, name);
  if (!match) return name;

  // Build highlighted name with matched substring
  const before = name.substring(0, match.index);
  const matched = name.substring(match.index, match.index + match.length);
  const after = name.substring(match.index + match.length);

  return (
    <>
      { before }
      <strong style={ { color: '#000', fontWeight: 'bold' } }>{ matched }</strong>
      { after }
    </>
  );
};

const SearchBar = ({ /* history */ isToggable }) => {
  const { query } = queryString.parse(window.location.search);
  const [kind, setKind] = useState(window.location.pathname.match(/^(\/walls|\/uk\/walls)/) ? WALLS : WORKS);
  const [term, setTerm] = useState(query);
  const [autocompleteItems, setAutocompleteItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const ref = createRef(null);
  const dropdownRef = useRef(null);
  const isFetchingRef = useRef(false);

  const body = document.getElementsByTagName('body')[0];

  // Fetch artists and designers from API or localStorage
  useEffect(() => {
    const fetchAutocompleteData = async () => {
      // Skip if autocomplete is disabled
      if (!window.autoCompleteEnabled) {
        return;
      }

      // Check if already fetching
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);

      try {
        const data = await loadAutocompleteDataWithCache();
        setAutocompleteItems(data);
        setIsFetched(true);
      } catch (error) {
        console.error('Error fetching autocomplete data:', error);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchAutocompleteData();
  }, []);

  // Filter autocomplete items based on term
  const filterItems = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1 || !isFetched) {
      return [];
    }

    return autocompleteItems
      .map((item) => ({
        ...item,
        score: calculateMatchScore(searchTerm, item.name)
      }))
      .filter((item) => item.score > 0) // Only include matches
      .sort((a, b) => b.score - a.score) // Sort by score descending (best first)
      .slice(0, 10); // Max 10 items
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const onToggle = (e) => {
    e.preventDefault();
    body.classList.toggle('search-visible');
    if (ref.current) { ref.current.focus(); }
  };

  const performSearch = () => {
    const newQuery = term ? term.trim() : '';

    if (!newQuery) {
      return;
    }

    const params = { query: newQuery };
    if (kind === WALLS) {
      params.as = THUMB;
    }
    window.location.href = `${ window.currentLocale.basename }/${ kind }?${ queryString.stringify(params) }`;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // Handle autocomplete selection
    if (showDropdown && filteredItems.length > 0) {
      if (selectedIndex === 0) {
        // "Search art" is selected, proceed with normal search
        performSearch();
      } else {
        // Artist or designer is selected, navigate to their page
        const item = filteredItems[selectedIndex - 1];
        if (item) {
          window.location.href = `${ window.currentLocale.basename }${ item.path }`;
        }
      }
    } else {
      performSearch();
    }
  };

  const onChangeKind = (event, { value }) => {
    setKind(value);
    // Hide dropdown when switching to walls
    if (value === WALLS) {
      setShowDropdown(false);
    }
  };

  const onChangeTerm = (event, { value }) => {
    setTerm(value);

    // Filter and show dropdown only for art searches, not walls
    if (value && value.length >= 1 && isFetched && kind === WORKS) {
      const filtered = filterItems(value);
      setFilteredItems(filtered);
      setShowDropdown(true);
      setSelectedIndex(0); // Reset to "Search art"
    } else {
      setShowDropdown(false);
      setFilteredItems([]);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || filteredItems.length === 0) {
      return;
    }

    const totalItems = filteredItems.length + 1; // +1 for "Search art"

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowDropdown(false);
    }
  };

  const handleSelectItem = (index) => {
    setSelectedIndex(index);
    if (index === 0) {
      // "Search art" selected
      performSearch();
    } else {
      // Artist or designer selected
      const item = filteredItems[index - 1];
      if (item) {
        window.location.href = `${ window.currentLocale.basename }${ item.path }`;
      }
    }
  };

  const form = (
    <div ref={ dropdownRef } style={ { position: 'relative', width: '100%' } }>
      <form onSubmit={ onSubmit }>
        <Input
          labelPosition="left"
          aria-label="Search"
          placeholder="Search Artfully Walls"
          defaultValue={ term }
          onChange={ onChangeTerm }
          onKeyDown={ handleKeyDown }
          ref={ ref }
          label={
            <Dropdown defaultValue={ kind } options={ OPTIONS } onChange={ onChangeKind } aria-label="Select" />
          }
          action={ { icon: 'search', 'aria-label': 'Search' } }
        />
        {
          isToggable ? (
            <button className={ Classes.cancel } type="button" onClick={ onToggle }>&times;</button>
          ) : null
        }
      </form>
      {
        showDropdown && isFetched && term && term.length >= 1 && kind === WORKS && (
          <div className={ Classes.autocompleteDropdown }>
            <div
              className={ classnames(Classes.autocompleteItem, Classes.searchArtItem, {
                [Classes.selected]: selectedIndex === 0
              }) }
              onClick={ () => handleSelectItem(0) }
              onMouseEnter={ () => setSelectedIndex(0) }
            >
              <i className="icon-search" />
              <span className={ Classes.searchArtText }>
                Search art:&nbsp;
                <strong>{ term }</strong>
              </span>
            </div>
            {
              filteredItems.map((item, index) => (
                <div
                  key={ item.path }
                  className={ classnames(Classes.autocompleteItem, Classes.artistItem, {
                    [Classes.selected]: selectedIndex === index + 1
                  }) }
                  onClick={ () => handleSelectItem(index + 1) }
                  onMouseEnter={ () => setSelectedIndex(index + 1) }
                >
                  {
                    item.image && (
                      <img
                        src={ item.image }
                        alt={ item.name }
                        className={ Classes.artistImage }
                      />
                    )
                  }
                  <div className={ Classes.itemInfo }>
                    <span className={ Classes.itemName }>{ highlightMatch(term, item.name) }</span>
                    <span className={ Classes.itemType }>
                      { item.type === 'artist' ? 'Artist' : 'Designer' }
                    </span>
                  </div>
                </div>
              ))
            }
          </div>
        )
      }
    </div>
  );

  if (isToggable) {
    return (
      <>
        <a href="#" onClick={ onToggle } alt="Search">
          <i className="icon-search" />
        </a>
        <div className={ classnames(AppClasses.container20, Classes.searchContainer) }>
          <div className={ classnames(AppClasses.innerContainer20, Classes.search) }>
            { form }
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={ classnames(Classes.search, Classes.permSearch) }>
      { form }
    </div>
  );
};

SearchBar.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  isToggable: PropTypes.bool
};

export default SearchBar;
